import React, { useContext } from 'react';
import { compact } from 'lodash';
import type { ImageDetails } from 'venn-api';
import { DEFAULT_STOCK_PHOTO, STOCK_PHOTOS } from 'venn-state';
import { Icon } from 'venn-ui-kit';
import { buttonize, useHasFF } from 'venn-utils';
import { PlusIcon, UploadImageText } from '../shared';
import { validate as uuidValidate } from 'uuid';
import { useSectionPageImages, useStudioImages } from '../../logic/useStudioImages';
import { ThemeContext } from 'styled-components';

type BackgroundImageGridProps = {
  page: PageWithBackgroundImage;
  singleColumnView: boolean;
  canCreateTemplate: boolean;
};

export enum PageWithBackgroundImage {
  TITLE_PAGE,
  SECTION_PAGE,
}

const determineImageBorder = (isImageSelected: boolean, isRcmCustomization: boolean, mainColor: string) => {
  // It is important NOT to use string templates (using the ` character), because Tailwind does not properly handle dynamically generated classes.
  if (isImageSelected) {
    return mainColor === 'dark-blue'
      ? 'ring-[3px] ring-inset ring-venn-dark-blue'
      : 'ring-[3px] ring-inset ring-venn-primary-main';
  }
  return isRcmCustomization ? 'ring-[1px] ring-inset ring-venn-grey-30' : '';
};

export const BackgroundImageGrid = ({ page, singleColumnView, canCreateTemplate }: BackgroundImageGridProps) => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const studioImages = useStudioImages();
  const sectionImages = useSectionPageImages();
  const theme = useContext(ThemeContext);
  const mainColor = hasCustomizationEnhancements ? 'dark-blue' : 'primary-main';

  const { images, imageUploading, uploadReportImage, backgroundImage, setBackgroundImage } =
    page === PageWithBackgroundImage.SECTION_PAGE ? sectionImages : studioImages;

  const uploadInputID =
    page === PageWithBackgroundImage.SECTION_PAGE ? 'sectionImageUploadFileDialogId' : 'imageUploadFileDialogId';

  const coverPageImageId = uuidValidate(backgroundImage) ? backgroundImage : DEFAULT_STOCK_PHOTO;
  const getImageURL = (imageId: string) => `/api/images/${imageId}`;

  const plusIcon = hasCustomizationEnhancements ? (
    <Icon prefix="far" type="square-plus" className="text-[20px] !font-light" />
  ) : (
    <Icon prefix="fa" type="plus" />
  );
  const defaultBadge = (
    <Icon
      type="check-circle"
      className={`absolute bottom-[6px] right-[6px] text-[14px] z-[10000] text-venn-${mainColor}`}
    />
  );

  return (
    <>
      <div className="flex flex-row flex-wrap w-full items-center">
        {compact([...STOCK_PHOTOS, ...(images ?? [])]).map((image: ImageDetails) => (
          <button
            key={image.id}
            type="button"
            onClick={() => canCreateTemplate && setBackgroundImage(image.id)}
            className={`inline-block relative h-[100px] ${singleColumnView ? 'w-full' : 'w-[47%]'} 
              ml-0 mr-[3%] my-[1.5%] order-1 bg-center bg-cover object-cover cursor-pointer ${determineImageBorder(image.id === coverPageImageId, hasCustomizationEnhancements, mainColor)}
              `}
            style={{ backgroundImage: `url(${getImageURL(image.id)})` }}
          >
            {image.id === coverPageImageId ? defaultBadge : null}
          </button>
        ))}
      </div>
      <UploadImageText
        disabled={imageUploading || !canCreateTemplate}
        customFontSize={hasCustomizationEnhancements ? '12px' : undefined}
        customColor={hasCustomizationEnhancements ? theme.Colors.DarkBlue : undefined}
        {...buttonize(() => canCreateTemplate && document.getElementById(uploadInputID)?.click())}
      >
        <PlusIcon marginless={hasCustomizationEnhancements}>{plusIcon}</PlusIcon>
        Upload Image
      </UploadImageText>
      <input
        type="file"
        id={uploadInputID}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={(file) => uploadReportImage(file.target, true)}
      />
    </>
  );
};
